import {
  discriminatedUnion,
  enum as zodEnum,
  iso,
  literal,
  number,
  object,
  type output,
  string,
} from 'zod';
import { winnerEventRoles, winnerEventTypes } from '../winner-event';

export const MAX_WINNER_EVENT_NAME_LENGTH = 100;
export const MIN_WINNER_EVENT_ATTENDEES = 1;
export const MAX_WINNER_EVENT_ATTENDEES = 10;

export const winnerEventTypeSchema = zodEnum(winnerEventTypes, {
  error: `Select one of these winner event types: ${winnerEventTypes.join(', ')}.`,
});

export const winnerEventRoleSchema = zodEnum(winnerEventRoles, {
  error: `Select one of these winner event roles: ${winnerEventRoles.join(', ')}.`,
});

const winnerEventRequestFieldsSchema = object({
  displayName: string()
    .min(1, 'Enter a winner event name.')
    .max(MAX_WINNER_EVENT_NAME_LENGTH, `Use ${MAX_WINNER_EVENT_NAME_LENGTH} characters or fewer for the winner event name.`),
  eventType: winnerEventTypeSchema,
  eventDate: iso.date({
    error: 'Enter the winner event date in YYYY-MM-DD format.',
  }),
  role: winnerEventRoleSchema,
  numberOfAttendees: number({
    error: `Enter a whole number from ${MIN_WINNER_EVENT_ATTENDEES} to ${MAX_WINNER_EVENT_ATTENDEES} for the number of attendees.`,
  })
    .int(`Enter a whole number from ${MIN_WINNER_EVENT_ATTENDEES} to ${MAX_WINNER_EVENT_ATTENDEES} for the number of attendees.`)
    .min(MIN_WINNER_EVENT_ATTENDEES, `Enter a whole number from ${MIN_WINNER_EVENT_ATTENDEES} to ${MAX_WINNER_EVENT_ATTENDEES} for the number of attendees.`)
    .max(MAX_WINNER_EVENT_ATTENDEES, `Enter a whole number from ${MIN_WINNER_EVENT_ATTENDEES} to ${MAX_WINNER_EVENT_ATTENDEES} for the number of attendees.`),
});

const winnerEventTimeSchema = discriminatedUnion('isTimeTBD', [
  object({
    isTimeTBD: literal(true),
    startTime: string().optional(),
    endTime: string().optional(),
  }),
  object({
    isTimeTBD: literal(false),
    startTime: string({
      error: 'Enter a start time when the time is not TBD.',
    }).min(1, 'Enter a start time when the time is not TBD.'),
    endTime: string({
      error: 'Enter an end time when the time is not TBD.',
    }).min(1, 'Enter an end time when the time is not TBD.'),
  }),
]);

export const winnerEventRequestSchema = winnerEventRequestFieldsSchema.and(winnerEventTimeSchema);

type WinnerEventRequestFields = output<typeof winnerEventRequestFieldsSchema>;

export type WinnerEventRequest = WinnerEventRequestFields & {
  startTime?: string;
  endTime?: string;
  isTimeTBD: boolean;
};
